import {createSlice} from '@reduxjs/toolkit';
import {FilteredPayloadAction, reducerUtils} from '@wallaby-ui/core';

import {
  DEFAULT_GRID_PAGE_SIZE,
  GRID_REDUCER_STATE_ID,
  GRID_ACTION_FILTER,
  GRID_ACTION_FILTER_BUTTONS,
  GRID_ACTION_PAGE,
  GRID_ACTION_SCROLL,
  GRID_ACTION_SORT,
} from './grid_constants';
import {MemorizedColumnStates} from './grid_types';
import {TableConfigRaw} from 'src/js/model/table_config';

export interface PerformedAction {
  type: string;
}

export interface GridPagination {
  page: number;
  pages: number;
  pageSize: number;
}

export interface GridParams {
  filters?: any[];
  scroll?: [number, number];
  sort?: any;
  pagination?: GridPagination;
  page?: number;
  size?: number;
  filteredRowsCount?: number;
  filterButtonId?: number;
}

export interface GridState {
  gridParams: Omit<GridParams, 'page' | 'size'>;
  gridConfig: TableConfigRaw | undefined;
  performedActions: PerformedAction[];
  memorizedColumnStates: MemorizedColumnStates;
}

export interface InitGridParamsPayload {
  params: {
    defaultPagination: GridPagination;
  };
}

export interface ChangeGridParamsPayload {
  params: GridParams;
}

export interface SetGridConfigPayload {
  reload: boolean;
  config: any;
}

export interface UpdatePerformedActionPayload {
  performedAction: PerformedAction['type'];
  remove: boolean;
  reset: boolean;
}

const initialState: GridState = {
  memorizedColumnStates: {},
  gridConfig: undefined,
  gridParams: {
    filters: [],
    scroll: [0, 0],
    sort: undefined,
    pagination: {
      page: 1,
      pages: 0,
      pageSize: DEFAULT_GRID_PAGE_SIZE,
    },
    filteredRowsCount: 0,
    filterButtonId: 0,
  },
  performedActions: [],
};

export const gridSlice = createSlice({
  name: GRID_REDUCER_STATE_ID,
  initialState,
  reducers: {
    memorizeColumnStates: {
      reducer(state, action: FilteredPayloadAction<MemorizedColumnStates>) {
        state.memorizedColumnStates = action.payload;
      },
      prepare: reducerUtils.prepareWithStateIdFactory<MemorizedColumnStates>(),
    },
    setGridConfig: {
      reducer(state, action: FilteredPayloadAction<SetGridConfigPayload>) {
        if (
          action.payload.reload ||
          (action.payload.config && !state.gridConfig)
        ) {
          state.gridConfig = action.payload.config;
        }
      },
      prepare: reducerUtils.prepareWithStateIdFactory<SetGridConfigPayload>(),
    },
    initGridParams: {
      reducer(state, action: FilteredPayloadAction<InitGridParamsPayload>) {
        const {params} = action.payload;
        const {defaultPagination} = params;

        state.gridParams.pagination = {
          ...state.gridParams.pagination,
          ...defaultPagination,
        };
      },
      prepare: reducerUtils.prepareWithStateIdFactory<InitGridParamsPayload>(),
    },
    changeGridParams: {
      reducer(state, action: FilteredPayloadAction<ChangeGridParamsPayload>) {
        const {params} = action.payload;

        if (params.page) {
          state.performedActions.push({type: GRID_ACTION_PAGE});
        }
        if (params.pagination) {
          state.performedActions.push({type: GRID_ACTION_PAGE});
        }
        if (params.scroll) {
          state.performedActions.push({type: GRID_ACTION_SCROLL});
        }
        if (params.sort) {
          state.performedActions.push({type: GRID_ACTION_SORT});
        }
        if (params.filters) {
          state.performedActions.push({type: GRID_ACTION_FILTER});
        }
        if (params.filterButtonId) {
          state.performedActions.push({type: GRID_ACTION_FILTER_BUTTONS});
        }

        state.gridParams.pagination = {
          ...state.gridParams.pagination,
          ...(params.page ? {page: params.page} : {}),
          ...(params.size ? {pageSize: params.size} : {}),
          ...params.pagination,
        };
        state.gridParams = {
          ...state.gridParams,
          ...(params.scroll ? {scroll: params.scroll} : {}),
          ...(params.sort ? {sort: [...params.sort]} : {}),
          ...(params.filters ? {filters: [...params.filters]} : {}),
          ...(params.filteredRowsCount
            ? {filteredRowsCount: params.filteredRowsCount}
            : {}),
          ...(params.filterButtonId || params.filterButtonId === 0
            ? {filterButtonId: params.filterButtonId}
            : {}),
        };
      },
      prepare:
        reducerUtils.prepareWithStateIdFactory<ChangeGridParamsPayload>(),
    },
    updatePerformedAction: {
      reducer(
        state,
        action: FilteredPayloadAction<UpdatePerformedActionPayload>
      ) {
        const {performedAction, remove, reset} = action.payload;
        if (!reset) {
          if (remove) {
            state.performedActions = state.performedActions?.filter(
              (item) => item.type === performedAction
            );
          } else {
            state.performedActions.push({type: performedAction});
          }
        }
      },
      prepare:
        reducerUtils.prepareWithStateIdFactory<UpdatePerformedActionPayload>(),
    },
  },
});
